
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LeftAuth from "../components/leftAuth";
import FormInput from "../../../shared/components/ui/form/FormInput";
import PasswordField from "../../../shared/components/ui/form/PasswordField";
import { useAuth } from "../../../shared/hooks/useAuth";
import { useFormValidation, validationRules } from "../../../shared/hooks/useFormValidation";
import { selectIsAuthenticated } from "../../../app/store/slices/authSlice";

const Login = () => {
  const navigate = useNavigate();
  const { login, isLoading, error } = useAuth();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const { values, errors, handleChange, handleBlur, validate } = useFormValidation(
    { email: "", password: "" },
    {
      email: validationRules.email,
      password: validationRules.required("Password"),
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      const data = await login(values.email, values.password);
      if (data) {
        navigate("/admin/dashboard"); // Redirect after successful login
      }
    }
  };

  // Prevent flash - don't render form if authenticated
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 grow bg-[#F7F7F8] flex justify-center items-center">
      {/* Left Section */}
      <LeftAuth />

      {/* Right Section */}
      <div className="max-w-[390px] w-full mx-auto mt-10">
        <form className="card-body flex flex-col gap-5 p-5" onSubmit={handleSubmit}>
          <div className="text-center mb-2.5">
            <h3 className="text-4xl font-bold text-[#232526] leading-none mb-2.5">Login</h3>
          </div>

          {/* Email */}
          <FormInput label="Email Address" name="email" type="email" value={values.email} onChange={handleChange} onBlur={handleBlur} error={errors.email} placeholder="Enter your email" required />

          {/* Password */}
          <PasswordField label="Password" name="password" value={values.password} onChange={handleChange} onBlur={handleBlur} error={errors.password} placeholder="Enter your password" required />

          {/* Remember Me + Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center">
              <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="ml-2 text-sm text-[#232526]">Remember me</span>
            </label>

            <button type="button" onClick={() => navigate("/forgot-password")} className="text-sm text-[#4057FE] hover:underline">
              Forgot password?
            </button>
          </div>

          {/* Sign In Button */}
          <button type="submit" disabled={isLoading} className="py-3 common-gradiant text-white rounded-xl font-semibold disabled:opacity-50">
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Signing in...
              </div>
            ) : (
              "Sign In"
            )}
          </button>

          {/* Sign Up */}
          <div className="text-center">
            <p className="text-sm text-[#232526]">
              Don't have an account?{" "}
              <button type="button" onClick={() => navigate("/signup")} className="text-[#4057FE] underline font-bold">
                Sign up here
              </button>
            </p>
          </div>

          {/* Backend error display */}
          {error && <p className="text-center text-red-500 text-sm">{error}</p>}
        </form>
      </div>
    </div>
  );
};

export default Login;
